import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeGranularity } from "@@/shared/date-time/time-granularity";
import { timeGranularityZodSchema } from "@@/shared/date-time/time-granularity";
import { durationInMinutesZodSchema } from "@@/shared/date-time/time-spans";
import { multiplyPrice } from "@@/shared/prices/multiply-price";
import type { Price } from "@@/shared/prices/price";
import { priceZodSchema, zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Branded } from "@@/utilities/branded";
import { genericId } from "@@/utilities/generic-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ResourcePriceId } from "./resource-price-id";
import {
    resourcePriceIdFactory,
    resourcePriceIdZodSchema,
} from "./resource-price-id";

type ResourcePriceStepId = Branded<string, "ResourcePriceStepId">;
const resourcePriceStepIdPrefix = "step__";
const {
    factory: resourcePriceStepIdFactory,
    //assert: isResourcePriceId,
    zodSchema: resourcePriceStepIdZodSchema,
} = genericId<ResourcePriceStepId>(resourcePriceStepIdPrefix, {
    regexMatch: /(^step_.*$)/,
});

// Define a type for a price step
// type PriceStep = {
//     readonly _id: ResourcePriceId;

//     readonly startMinute: DurationInMinutes; // Inclusive
//     readonly endMinute: DurationInMinutes | null; // Exclusive, null means infinity
//     readonly price: Price;
// };

// Zod schema for PriceStep
const priceStepZodSchema: z.ZodSchema = z.object({
    _id: resourcePriceStepIdZodSchema,
    startMinute: durationInMinutesZodSchema,
    endMinute: durationInMinutesZodSchema.nullable(),
    price: priceZodSchema,
});

type PriceStep = z.infer<typeof priceStepZodSchema>;

// Create the main type for stepped pricing
type ResourcePriceByTimeSteps = {
    readonly _id: ResourcePriceId;
    readonly _type: "RESOURCE_PRICE_BY_TIME_STEPS";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly granularity: TimeGranularity;
    readonly steps: ReadonlyArray<PriceStep>;
};

// Factory parameters type with optional ID
type resourcePriceByTimeStepsFactoryParams = Except<
    ResourcePriceByTimeSteps,
    "_id" | "_type" | "_createdAt"
> & { _id?: ResourcePriceId };

// Factory function to create a ResourcePriceByTimeSteps instance
const resourcePriceByTimeStepsFactory = (
    params: resourcePriceByTimeStepsFactoryParams,
): ResourcePriceByTimeSteps => {
    return {
        ...params,
        _id: params._id || resourcePriceIdFactory(),
        _type: "RESOURCE_PRICE_BY_TIME_STEPS",
        _createdAt: nowIsoAndUnix(),
    };
};

const resourceStepFactory = (params: Except<PriceStep, "_id">): PriceStep => {
    return {
        ...params,
        _id: resourcePriceStepIdFactory(),
    };
};

// Type guard function

const isResourcePriceByTimeSteps = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is ResourcePriceByTimeSteps => {
    return item._type === "RESOURCE_PRICE_BY_TIME_STEPS";
};

// Zod schema for validation
// Zod schema for validation
const resourcePriceByTimeStepsZodSchema: z.ZodSchema<ResourcePriceByTimeSteps> =
    z.object({
        _id: resourcePriceIdZodSchema,
        _type: z.literal("RESOURCE_PRICE_BY_TIME_STEPS"),
        _createdAt: isoAndUnixZodSchema,
        granularity: timeGranularityZodSchema,
        steps: z
            .array(priceStepZodSchema)
            .min(1)
            .refine(
                steps => {
                    // Ensure steps are ordered by startMinute
                    for (let i = 0; i < steps.length - 1; i++) {
                        if (steps[i].startMinute >= steps[i + 1].startMinute) {
                            return false;
                        }

                        const stepEndMinute = steps[i].endMinute;
                        // Ensure endMinute is greater than startMinute when not null
                        if (
                            stepEndMinute !== null &&
                            stepEndMinute <= steps[i].startMinute
                        ) {
                            return false;
                        }

                        // Ensure each step's endMinute matches the next step's startMinute when not null
                        if (
                            stepEndMinute !== null &&
                            stepEndMinute !== steps[i + 1].startMinute
                        ) {
                            return false;
                        }
                    }

                    // Check the last step's endMinute is greater than startMinute when not null
                    const lastStep = steps[steps.length - 1];
                    if (
                        lastStep.endMinute !== null &&
                        lastStep.endMinute <= lastStep.startMinute
                    ) {
                        return false;
                    }

                    return true;
                },
                {
                    message: "Price steps must be ordered and continuous",
                },
            ),
    });

const calculateTotalPrice = (
    priceWithSteps: ResourcePriceByTimeSteps,
    durationMinutes: number,
): Price => {
    if (durationMinutes <= 0 || !priceWithSteps.steps.length) {
        return zeroPrice;
    }

    let totalPrice = zeroPrice;
    let remainingMinutes = durationMinutes;

    for (const step of priceWithSteps.steps) {
        if (remainingMinutes <= 0) {
            break;
        }

        const stepStartMinute = step.startMinute;
        const stepEndMinute =
            step.endMinute === null ? Infinity : step.endMinute;

        // Skip this step if we haven't reached its start time yet
        if (durationMinutes <= stepStartMinute) {
            continue;
        }

        // Calculate minutes spent in this step
        const minutesInStep = Math.min(
            remainingMinutes, // Remaining minutes to be billed
            stepEndMinute - stepStartMinute, // Size of this step
            durationMinutes - stepStartMinute, // How far into this step the duration goes
        );

        if (minutesInStep > 0) {
            totalPrice = sumPrices([totalPrice, step.price]);

            // Reduce remaining minutes
            remainingMinutes -= minutesInStep;
        }
    }

    if (remainingMinutes > 0) {
        //Use the last step's price for any remaining minutes

        const step = priceWithSteps.steps[priceWithSteps.steps.length - 1];

        //How many steps are left
        const stepsLeft = step.endMinute
            ? Math.ceil(remainingMinutes / (step.endMinute - step.startMinute))
            : 1;

        const priceForRemainingSteps = multiplyPrice(step.price, stepsLeft);
        totalPrice = sumPrices([totalPrice, priceForRemainingSteps]);
    }

    return totalPrice;
};

export {
    calculateTotalPrice,
    isResourcePriceByTimeSteps,
    resourcePriceByTimeStepsFactory,
    resourcePriceByTimeStepsZodSchema,
    resourceStepFactory,
};
export type { PriceStep, ResourcePriceByTimeSteps };
